import type { ProductRecommendation } from '@/types';
import { BonusParser } from './bonus-parser';

export class ProductEngine {
  private recommendations: Map<string, Set<string>> = new Map();
  private exclusions: Set<string> = new Set();
  private alternatives: Map<string, string> = new Map();
  private productCounts: Map<string, number> = new Map();
  private totalPoints = 0;

  /**
   * Add a recommendation from a question answer
   */
  addRecommendation(questionCode: string, products: string[], points: number = 0): void {
    if (!this.recommendations.has(questionCode)) {
      this.recommendations.set(questionCode, new Set());
    }

    products.forEach(product => {
      this.recommendations.get(questionCode)!.add(product);
      this.incrementProductCount(product);
    });

    // Add points
    this.totalPoints += points;
    console.log(`📊 Points added: +${points} (Total: ${this.totalPoints})`);

    // Emit product added event
    this.emitEvent('pemlab-product-added', {
      products,
      points,
    });
  }

  /**
   * Add exclusions
   */
  addExclusion(products: string[]): void {
    products.forEach(product => this.exclusions.add(product));
    console.log(`🚫 Exclusions added:`, products);
  }

  /**
   * Add alternative for an excluded product
   */
  addAlternative(original: string, alternative: string): void {
    this.alternatives.set(original, alternative);
    console.log(`🔄 Alternative set: ${original} → ${alternative}`);
  }

  /**
   * Remove a recommendation (e.g., when user deselects an answer)
   */
  removeRecommendation(questionCode: string, products: string[], points: number = 0): void {
    if (this.recommendations.has(questionCode)) {
      products.forEach(product => {
        this.recommendations.get(questionCode)!.delete(product);
        this.decrementProductCount(product);
      });

      // If no more products for this question, remove the entry
      if (this.recommendations.get(questionCode)!.size === 0) {
        this.recommendations.delete(questionCode);
      }
    }

    // Remove points
    this.totalPoints = Math.max(0, this.totalPoints - points);
    console.log(`📊 Points removed: -${points} (Total: ${this.totalPoints})`);

    // Emit product removed event
    this.emitEvent('pemlab-product-removed', {
      products,
      points,
    });
  }

  /**
   * Get final product recommendations
   */
  getRecommendations(): ProductRecommendation[] {
    const finalProducts = new Map<string, ProductRecommendation>();

    // Process recommendations from answers
    this.recommendations.forEach((products, questionCode) => {
      products.forEach(product => {
        // Check exclusions
        if (this.exclusions.has(product)) {
          // Look for alternatives
          if (this.alternatives.has(product)) {
            const replacements = this.alternatives.get(product)!;
            console.log(`🔄 Replacing ${product} with ${replacements}`);

            // Handle multiple replacement products separated by |
            const replacementProducts = replacements.split(' | ').filter(p => p);
            
            replacementProducts.forEach(replacementProduct => {
              if (!finalProducts.has(replacementProduct)) {
                finalProducts.set(replacementProduct, {
                  code: replacementProduct,
                  name: this.getProductName(replacementProduct),
                  count: 0,
                  reason: [],
                });
              }

              const rec = finalProducts.get(replacementProduct)!;
              rec.count = (rec.count || 0) + 1;
              rec.reason.push(questionCode);
            });

            // Emit product replaced event
            this.emitEvent('pemlab-product-replaced', {
              products: [product],
              replacedBy: replacements,
            });
          } else {
            // Product excluded without alternative
            console.log(`🚫 Excluding ${product} (no alternative)`);
          }
          return; // Skip the excluded product
        }

        // Normal product (not excluded)
        if (!finalProducts.has(product)) {
          finalProducts.set(product, {
            code: product,
            name: this.getProductName(product),
            count: 0,
            reason: [],
          });
        }

        const rec = finalProducts.get(product)!;
        rec.count = this.productCounts.get(product) || 1;
        rec.reason.push(questionCode);
      });
    });

    // Get base products array
    const baseProducts = Array.from(finalProducts.values());

    // Add bonus products based on points
    const bonusProducts = BonusParser.getBonusProducts(this.totalPoints);
    console.log(`🎁 Bonus products for ${this.totalPoints} points:`, bonusProducts);

    // Merge bonus products (checking exclusions)
    bonusProducts.forEach(bonusProduct => {
      // Check if bonus product is excluded
      if (this.exclusions.has(bonusProduct)) {
        console.log(`🚫 Bonus product ${bonusProduct} is excluded`);
        
        // Check for alternatives
        if (this.alternatives.has(bonusProduct)) {
          const replacements = this.alternatives.get(bonusProduct)!;
          console.log(`🔄 Replacing bonus ${bonusProduct} with ${replacements}`);
          
          // Handle multiple replacement products
          const replacementProducts = replacements.split(' | ').filter(p => p);
          
          replacementProducts.forEach(replacementProduct => {
            if (!finalProducts.has(replacementProduct)) {
              baseProducts.push({
                code: replacementProduct,
                name: this.getProductName(replacementProduct),
                count: 1,
                reason: ['bonus-points-replacement'],
              });
              
              // Emit replaced event
              this.emitEvent('pemlab-product-replaced', {
                oldProduct: bonusProduct,
                newProduct: replacementProduct,
                reason: 'bonus-exclusion',
              });
            }
          });
        }
      } else if (!finalProducts.has(bonusProduct)) {
        // Only add if not excluded and not already present
        baseProducts.push({
          code: bonusProduct,
          name: this.getProductName(bonusProduct),
          count: 1,
          reason: ['bonus-points'],
        });

        // Emit bonus added event
        this.emitEvent('pemlab-bonus-added', {
          products: [bonusProduct],
          points: this.totalPoints,
        });
      }
    });

    // Sort by count (most recommended first)
    return baseProducts.sort((a, b) => b.count - a.count);
  }

  /**
   * Get total points accumulated
   */
  getTotalPoints(): number {
    return this.totalPoints;
  }

  /**
   * Get list of unique product codes only
   */
  getProductCodes(): string[] {
    return this.getRecommendations().map(rec => rec.code);
  }

  /**
   * Convert product code to readable name
   */
  private getProductName(code: string): string {
    return code
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /**
   * Increment product count
   */
  private incrementProductCount(product: string): void {
    const current = this.productCounts.get(product) || 0;
    this.productCounts.set(product, current + 1);
  }

  /**
   * Decrement product count
   */
  private decrementProductCount(product: string): void {
    const current = this.productCounts.get(product) || 0;
    if (current > 0) {
      this.productCounts.set(product, current - 1);
    }
  }

  /**
   * Reset the engine (for new session)
   */
  reset(): void {
    this.recommendations.clear();
    this.exclusions.clear();
    this.alternatives.clear();
    this.productCounts.clear();
    this.totalPoints = 0;
    console.log('🔄 Product engine reset');
  }

  /**
   * Emit custom event for debug panel
   */
  private emitEvent(eventName: string, data: any): void {
    const event = new CustomEvent(eventName, {
      detail: {
        type: eventName.replace('pemlab-', '').replace('-', '_'),
        data,
      },
    });
    window.dispatchEvent(event);
  }

  /**
   * Get debug info
   */
  getDebugInfo(): {
    recommendations: Record<string, string[]>;
    exclusions: string[];
    alternatives: Record<string, string>;
    totalPoints: number;
  } {
    const recommendations: Record<string, string[]> = {};
    this.recommendations.forEach((products, key) => {
      recommendations[key] = Array.from(products);
    });

    return {
      recommendations,
      exclusions: Array.from(this.exclusions),
      alternatives: Object.fromEntries(this.alternatives),
      totalPoints: this.totalPoints,
    };
  }
}
